import React, { useEffect, useRef, useState } from "react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// NOTE: the global date picker does not allow future dates. There are no
// forward-looking presets and every preset's end is clamped to today, the
// calendar grid greys out days after today, and the date inputs carry a
// `max={today}`. Pages that genuinely need a forward-looking window (e.g.
// Event Intelligence) must not rely on this picker to produce one.
const PRESETS = [
  { key: "today", label: "Today", description: "Just today." },
  { key: "yesterday", label: "Yesterday", description: "Just yesterday." },
  { key: "7d", label: "Last 7 Days", description: "Rolling 7-day window ending today." },
  { key: "30d", label: "Last 30 Days", description: "Rolling 30-day window ending today." },
  { key: "this_month", label: "This Month", description: "Month-to-date — 1st through today (future days in the current month are not selectable)." },
  { key: "last_month", label: "Last Month", description: "Full previous calendar month." },
  { key: "this_quarter", label: "This Quarter", description: "Quarter-to-date — first day through today (future days are not selectable)." },
  { key: "custom", label: "Custom Range", description: "Pick any start and end date up to today." },
];

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fromDateStr = (s) => s ? new Date(s + "T00:00:00") : null;

const fmtDisplay = (s) => {
  if (!s) return "";
  const d = fromDateStr(s);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// "May 12 – Jun 10, 2026" when both ends share a year; otherwise both years shown.
const fmtRange = (start, end) => {
  if (!start || !end) return "";
  const s = fromDateStr(start);
  const e = fromDateStr(end);
  const startStr = s.getFullYear() === e.getFullYear()
    ? s.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : fmtDisplay(start);
  return `${startStr} – ${fmtDisplay(end)}`;
};

export function resolvePreset(key) {
  // 2026-08-20 — compute every preset's "today" in UTC, not browser-local
  // time. The backend's `guest_intel_sentences` rows and the `fn_get_*`
  // RPCs key/filter on UTC date boundaries, so the rolling 7d/30d window we
  // request must be UTC — otherwise the `${start}_${end}` date_range key
  // misses and the AI sentence layer silently disappears (worst for US/Canada
  // viewers in the evening, whose local "today" is up to a day behind UTC).
  // Window SHAPE is unchanged from the prior browser-local version
  // (start = today − (N−1), end = today, inclusive) — only the timezone
  // defining "today" changed. UI display (calendar grid, range label) still
  // uses the user's local calendar via toDateStr / fmtRange below; only the
  // returned range strings are UTC. Mirrors ViewContext's UTC default.
  // See item 2b in docs/GUEST-INTELLIGENCE-BACKEND-FOLLOWUPS.md.
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10); // YYYY-MM-DD in UTC
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth(); // 0-based
  const todayMs = Date.UTC(y, m, now.getUTCDate());
  const at = (days) => iso(new Date(todayMs + days * 86400000));
  // Future dates are not selectable — clamp any preset's end to UTC today so
  // month/quarter presets never leak past today.
  const clampToday = (s) => (s > at(0) ? at(0) : s);
  const monthDay = (yr, mo, day) => iso(new Date(Date.UTC(yr, mo, day)));

  switch (key) {
    case "today":
      return { start: at(0), end: at(0) };
    case "yesterday": {
      const d = at(-1);
      return { start: d, end: d };
    }
    case "7d":
      return { start: at(-6), end: at(0) };
    case "30d":
      return { start: at(-29), end: at(0) };
    case "this_month":
      return { start: monthDay(y, m, 1), end: clampToday(monthDay(y, m + 1, 0)) };
    case "last_month":
      return { start: monthDay(y, m - 1, 1), end: monthDay(y, m, 0) };
    case "this_quarter": {
      const q = Math.floor(m / 3) * 3;
      return { start: monthDay(y, q, 1), end: clampToday(monthDay(y, q + 3, 0)) };
    }
    default:
      return null;
  }
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

const CalendarMonth = ({ year, month, startDate, endDate, hoverDate, onDayClick, onDayHover }) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = toDateStr(new Date());

  const weeks = [];
  let week = Array(firstDay).fill(null);

  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const isInRange = (dateStr) => {
    if (!startDate || !endDate) return false;
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isInHoverRange = (dateStr) => {
    if (!startDate || endDate || !hoverDate) return false;
    const min = startDate < hoverDate ? startDate : hoverDate;
    const max = startDate > hoverDate ? startDate : hoverDate;
    return dateStr >= min && dateStr <= max;
  };

  return (
    <div>
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAYS.map((d) => (
          <div key={d} className="text-center text-sm font-medium text-gray-400 dark:text-gray-500 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0">
        {weeks.flat().map((day, i) => {
          if (!day) return <div key={`e${i}`} className="h-8" />;

          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isStart = dateStr === startDate;
          const isEnd = dateStr === endDate;
          const inRange = isInRange(dateStr);
          const inHover = isInHoverRange(dateStr);
          const isToday = dateStr === today;
          // Future dates are not selectable in the global picker.
          const isFuture = dateStr > today;

          return (
            <div
              key={i}
              onClick={isFuture ? undefined : () => onDayClick(dateStr)}
              onMouseEnter={isFuture ? undefined : () => onDayHover(dateStr)}
              className={`h-8 flex items-center justify-center text-sm transition-colors relative
                ${isFuture
                  ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                  : "cursor-pointer"
                }
                ${!isFuture && (isStart || isEnd)
                  ? "bg-[#32B1FF] text-white font-semibold rounded-md z-10"
                  : !isFuture && inRange
                  ? "bg-[#32B1FF]/15 text-gray-800 dark:text-gray-200"
                  : !isFuture && inHover
                  ? "bg-[#32B1FF]/10 text-gray-800 dark:text-gray-200"
                  : !isFuture && isToday
                  ? "text-[#32B1FF] font-semibold"
                  : !isFuture
                  ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  : ""
                }
                ${isStart && inRange ? "rounded-l-md" : ""}
                ${isEnd && inRange ? "rounded-r-md" : ""}
              `}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DateRangePicker = ({ startDate, endDate, onApply, activePreset: initialPreset, className = "", triggerClassName = "", disabled = false, hint = null }) => {
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const [hoverDate, setHoverDate] = useState(null);
  const [selecting, setSelecting] = useState(false); // true = picked start, waiting for end
  const [preset, setPreset] = useState(initialPreset || "this_month");
  const [includeToday, setIncludeToday] = useState(true);
  const ref = useRef(null);

  // Upper bound for the date inputs — no future dates in the global picker.
  const todayMax = toDateStr(new Date());

  const endD = fromDateStr(draftEnd || toDateStr(new Date()));
  const [viewYear, setViewYear] = useState(endD.getFullYear());
  const [viewMonth, setViewMonth] = useState(endD.getMonth());

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Sync draft with props when opening — so a previously cancelled selection
  // doesn't carry over. Resets dates AND the preset dropdown to the applied state.
  useEffect(() => {
    if (open) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
      setPreset(initialPreset || "this_month");
      setSelecting(false);
      setHoverDate(null);
      const d = fromDateStr(endDate || toDateStr(new Date()));
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [open]);

  // Keep the trigger label in sync when the parent updates activePreset
  // externally (e.g. a page setting its own default range on mount).
  useEffect(() => {
    if (initialPreset) setPreset(initialPreset);
  }, [initialPreset]);

  const handleDayClick = (dateStr) => {
    // Future dates are not selectable in the global picker.
    if (dateStr > toDateStr(new Date())) return;
    if (!selecting) {
      // First click — set start
      setDraftStart(dateStr);
      setDraftEnd(null);
      setSelecting(true);
      setPreset("custom");
    } else {
      // Second click — set end
      if (dateStr < draftStart) {
        setDraftEnd(draftStart);
        setDraftStart(dateStr);
      } else {
        setDraftEnd(dateStr);
      }
      setSelecting(false);
      setHoverDate(null);
      setPreset("custom");
    }
  };

  const handlePresetChange = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const resolved = resolvePreset(key);
    if (resolved) {
      setDraftStart(resolved.start);
      setDraftEnd(resolved.end);
      setSelecting(false);
      const d = fromDateStr(resolved.end);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  };

  const handleApply = () => {
    if (draftStart && draftEnd) {
      onApply(draftStart, draftEnd, preset);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const navigateMonth = (dir) => {
    let m = viewMonth + dir;
    let y = viewYear;
    if (m > 11) { m = 0; y++; }
    if (m < 0) { m = 11; y--; }
    setViewMonth(m);
    setViewYear(y);
  };

  // Display for the trigger button: preset name + the resolved date range,
  // so the concrete window is always visible (not just "Last 30 Days").
  // Derived from the APPLIED props (initialPreset + startDate/endDate), not the
  // live `preset`/draft state — so the trigger only updates on Apply, never
  // from an uncommitted dropdown selection.
  const presetLabel = initialPreset && initialPreset !== "custom"
    ? (PRESETS.find((p) => p.key === initialPreset)?.label || null)
    : null;
  const rangeText = fmtRange(startDate, endDate);

  return (
    <div className={`relative ${hint ? 'group' : ''} ${className}`} ref={ref}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        title={hint || undefined}
        aria-label={hint || undefined}
        className={`inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm outline-none focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent ${open ? 'ring-2 ring-[#32B1FF] border-transparent' : ''} ${disabled ? 'opacity-60 cursor-not-allowed hover:bg-white dark:hover:bg-gray-900' : ''} ${triggerClassName}`}
      >
        <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <span className="flex items-baseline gap-1.5 min-w-0 text-sm">
          {presetLabel && (
            <span className="font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">{presetLabel}</span>
          )}
          {rangeText ? (
            <span className={`whitespace-nowrap ${presetLabel ? "text-gray-500 dark:text-gray-400" : "font-medium text-gray-800 dark:text-gray-200"}`}>{rangeText}</span>
          ) : (!presetLabel && (
            <span className="font-medium text-gray-800 dark:text-gray-200">Select dates</span>
          ))}
        </span>
        <svg className="w-3 h-3 text-gray-400 ml-auto" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Hover/focus tooltip shown when the picker is disabled on a route that
          owns its own date window (e.g. Competitive Intelligence, Search Console,
          AI Monitor). Previously this was a static <p> label beneath the trigger;
          it is now a hover tooltip so the hint does not clutter the header on
          every visit. Mirrors the GA4DimensionNote tooltip pattern
          (group-hover / group-focus-within). The native `title`/`aria-label` on
          the trigger above are the accessible fallback. */}
      {hint && (
        <span
          role="tooltip"
          className="pointer-events-none absolute top-full mt-2 left-0 z-50 w-64 max-w-[calc(100vw-2rem)] rounded-lg bg-gray-900 dark:bg-gray-700 px-3 py-2 text-[11px] leading-snug text-gray-100 shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
        >
          {hint}
        </span>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full mt-2 right-0 z-50 w-full min-[500px]:w-[300px] min-[500px]:max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-5 flex flex-col gap-4 min-[555px]:flex-row min-[555px]:gap-6 min-[555px]:w-[520px]">

          {/* Left: Calendar */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-3">
              <button type="button" onClick={() => navigateMonth(-1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{MONTHS[viewMonth]} {viewYear}</span>
              <button type="button" onClick={() => navigateMonth(1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>

            <CalendarMonth
              year={viewYear} month={viewMonth}
              startDate={draftStart} endDate={selecting ? null : draftEnd}
              hoverDate={selecting ? hoverDate : null}
              onDayClick={handleDayClick}
              onDayHover={setHoverDate}
            />
          </div>

          {/* Right: Controls */}
          <div className="w-full border-t border-gray-200 dark:border-gray-700 pt-4 flex flex-col gap-4 min-[555px]:w-48 min-[555px]:border-t-0 min-[555px]:border-l min-[555px]:pt-0 min-[555px]:pl-5">
            {/* Preset dropdown */}
            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Date Range</label>
              <select
                value={preset}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-1 focus:ring-[#32B1FF] outline-none"
              >
                {PRESETS.map((p) => (
                  <option key={p.key} value={p.key} title={p.description}>{p.label}</option>
                ))}
              </select>
              {(() => {
                const activePreset = PRESETS.find((p) => p.key === preset);
                if (!activePreset?.description) return null;
                return (
                  <p className="mt-1.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                    {activePreset.description}
                  </p>
                );
              })()}
            </div>

            {/* Date inputs */}
            <div className="flex flex-col gap-2">
              <input
                type="date"
                value={draftStart || ""}
                max={todayMax}
                onChange={(e) => { const v = e.target.value; if (v && v > todayMax) return; setDraftStart(v); setPreset("custom"); }}
                className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 dark:[color-scheme:dark] outline-none focus:ring-1 focus:ring-[#32B1FF]"
              />
              <input
                type="date"
                value={draftEnd || ""}
                max={todayMax}
                onChange={(e) => { const v = e.target.value; if (v && v > todayMax) return; setDraftEnd(v); setPreset("custom"); }}
                className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 dark:[color-scheme:dark] outline-none focus:ring-1 focus:ring-[#32B1FF]"
              />
            </div>

            {/* Include today */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeToday}
                onChange={(e) => {
                  setIncludeToday(e.target.checked);
                  if (e.target.checked && draftEnd && draftEnd < toDateStr(new Date())) {
                    setDraftEnd(toDateStr(new Date()));
                    setPreset("custom");
                  }
                }}
                className="w-4 h-4 rounded border-gray-300 text-[#32B1FF] focus:ring-[#32B1FF]"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">Include Today</span>
            </label>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleApply}
                disabled={!draftStart || !draftEnd}
                className="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-[#32B1FF] text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
